import React, { useCallback, useMemo, useState } from 'react';
import { message, Button, Tooltip } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import AdminActions from '@components/AdminActions';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import { ITableTabs } from '@components/Table';
import api from '@services/api';
import { useTranslation } from 'react-i18next';
import InvoiceItems, {
  IItemData,
} from '@components/InvoiceStandardDetails/InvoiceItems';
import AddItemsToInvoice from '../../AddItemsToInvoice';
import EditItemsOnInvoice from '../../EditItemsOnInvoice';
import useNavigateToActionsMsg from '../../NavigateToActionsMsg';

interface IInvoiceItems {
  invoiceId?: string;
  customerId?: string;
  canManageItems?: boolean;
  canAddItems?: boolean;
  data: IItemData[];
  tabs?: ITableTabs;
}

const InvoiceItemsMot: React.FC<IInvoiceItems> = ({
  customerId,
  invoiceId,
  canManageItems,
  canAddItems,
  data,
  tabs,
}) => {
  const { t } = useTranslation('invoices');

  const [itemToEdit, setItemToEdit] = useState<IItemData>();
  const [showEditItem, setShowEditItem] = useState(false);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);

  const deleteItem = useCallback(
    (itemId: number) =>
      api.post('/service-proxy/broker/invoice-actions', {
        actions: [
          {
            itemId,
            customerId,
            invoiceId,
          },
        ],
        type: 'REMOVE_ITEM',
      }),
    [customerId, invoiceId],
  );

  const handleUpdateItem = useCallback((item: IItemData) => {
    setItemToEdit(item);
    setShowEditItem(true);
  }, []);

  const { navigateToActionsMsg } = useNavigateToActionsMsg();

  const handleDeleteItem = useCallback(
    async (id: number) => {
      try {
        const response = await deleteItem(id);
        navigateToActionsMsg(response.data.operationProtocol);
      } catch {
        message.error(t('invoiceItem.msgs.error-to-deleting'));
      }
    },
    [deleteItem, navigateToActionsMsg, t],
  );

  const extraColumns = useMemo(
    () =>
      canManageItems
        ? [
            {
              title: t('invoiceItem.actions'),
              align: 'center' as const,
              dataIndex: 'actions',
              render: (value: any, record: IItemData) => {
                const canEdit =
                  record?.blItemEdit === 'S' && record?.idItemFatura;
                return (
                  canEdit && (
                    <AdminActions
                      alertDelete={t('items-modal.delete.alert')}
                      entityToExclude={record.dsItem}
                      ruleToNotExclude={record.status === 'SYNCHRONIZED'}
                      onEdit={() => handleUpdateItem(record)}
                      onDelete={() => {
                        record?.idItemFatura &&
                          handleDeleteItem(record?.idItemFatura);
                      }}
                    />
                  )
                );
              },
            },
          ]
        : [],
    [canManageItems, t, handleUpdateItem, handleDeleteItem],
  );

  return (
    <>
      <InvoiceItems
        data={data}
        extraColumns={extraColumns}
        extraActions={
          canAddItems && (
            <RoleChecker role={rolesConstants.ADD_ITEMS_TO_INVOICE}>
              <Tooltip title={t('items-modal.new-items')}>
                <Button
                  icon={<PlusCircleOutlined />}
                  onClick={() => setShowAddItemsModal(true)}
                />
              </Tooltip>
            </RoleChecker>
          )
        }
        tabs={tabs}
      />

      {canManageItems && (
        <>
          <RoleChecker role={rolesConstants.ADD_ITEMS_TO_INVOICE}>
            <AddItemsToInvoice
              visible={showAddItemsModal}
              onClose={() => setShowAddItemsModal(false)}
              invoiceId={invoiceId}
              customerId={customerId}
            />
          </RoleChecker>

          <EditItemsOnInvoice
            modalVisible={showEditItem}
            itemToEdit={itemToEdit}
            invoiceId={invoiceId}
            customerId={customerId}
            onClose={() => setShowEditItem(false)}
          />
        </>
      )}
    </>
  );
};

export default InvoiceItemsMot;
